import { Children, useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import styles from "./SectionLayout.module.css";
import { columnsFor } from "./gridColumns";
import { ViewOptions } from "./ViewOptions";
import {
  SectionLayoutContext,
  readStoredSectionLayout,
  sectionLayoutModes,
  storeSectionLayout,
  useSectionLayout,
  type SectionLayoutMode,
} from "./sectionLayoutContext";

/** The columns each mode draws, shown on each layout's icon. */
const columnsByMode: Record<SectionLayoutMode, number> = { single: 1, loose: 2, dense: 3 };

/** The most columns each grid layout ever draws, however wide the screen. */
const maximumColumns: Record<Exclude<SectionLayoutMode, "single">, number> = { loose: 3, dense: 6 };

/** The narrowest a panel may be drawn in each grid layout; the grid fits as many columns as this allows. */
const minimumPanelWidth: Record<Exclude<SectionLayoutMode, "single">, number> = { loose: 560, dense: 400 };

/** Below this width the grid is one column, matching the switcher, which is hidden on narrower screens. */
const minimumGridWidth = 1300;

/** The gap between grid items (`--space-6`), used to work out how many columns fit. */
const gridGapPx = 24;

/**
 * Holds the viewer's section layout for everything rendered beneath it, and remembers the choice on
 * this browser.
 *
 * @param props.children - The screen whose sections the layout arranges.
 */
export function SectionLayoutProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [mode, setModeState] = useState<SectionLayoutMode>(readStoredSectionLayout);
  const setMode = useCallback((next: SectionLayoutMode) => {
    setModeState(next);
    storeSectionLayout(next);
  }, []);
  const value = useMemo(() => ({ mode, setMode }), [mode, setMode]);

  return <SectionLayoutContext.Provider value={value}>{children}</SectionLayoutContext.Provider>;
}

/**
 * The page's view controls (`ViewOptions`): the page's own controls (`leading`: My Groups' sort and filter), then the three
 * layouts, which close the pill once one is chosen. The layouts are offered only on screens wide enough for the grids, so on a
 * narrow screen the button shows only when there are leading controls to open.
 *
 * @param props.leading - The page's own controls, shown in the pill before the layouts.
 */
export function LayoutSwitcher({ leading }: { leading?: ReactNode }): React.JSX.Element {
  const { t } = useTranslation();
  const { mode, setMode } = useSectionLayout();

  return (
    <ViewOptions wideOnly={!leading}>
      {(close) => (
        <>
          {leading}
          <div className={`${styles.group} ${styles.layouts}`} role="group" aria-label={t("layout.label")}>
            {sectionLayoutModes.map((option) => (
              <button
                key={option}
                type="button"
                className={styles.option}
                aria-pressed={mode === option}
                onClick={() => {
                  setMode(option);
                  close();
                }}
              >
                <LayoutIcon columns={columnsByMode[option]} />
                <span>{t(`layout.${option}`)}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </ViewOptions>
  );
}

/**
 * The sections of a screen, laid out in the viewer's chosen layout. Each direct child is one grid
 * item, so a section that should span the whole width has to sit outside the grid.
 *
 * @param props.children - The sections to arrange.
 */
export function SectionGrid({ children }: { children: ReactNode }): React.JSX.Element {
  const { mode } = useSectionLayout();
  const gridRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  // Measured before the first paint: the observer only reports after it, and until then the grid would draw as one
  // full-width column (charts and all) and then snap into its columns.
  useLayoutEffect(() => {
    const element = gridRef.current;
    if (!element) {
      return;
    }

    setWidth(element.clientWidth);
    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // toArray drops the empty placeholders (a section that is not shown is `null` or `false`), so only panels that are drawn are counted.
  const panelCount = Children.toArray(children).length;
  const columns = mode === "single" ? 1 : columnsFor(panelCount, maximumColumnsFor(width, mode));

  return (
    <div
      ref={gridRef}
      className={styles.grid}
      data-section-layout={mode}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {children}
    </div>
  );
}

/** The most columns a grid of this width can take in the given layout, before the panel count is considered. */
function maximumColumnsFor(width: number, mode: Exclude<SectionLayoutMode, "single">): number {
  if (width < minimumGridWidth) {
    return 1;
  }

  const fitting = Math.floor((width + gridGapPx) / (minimumPanelWidth[mode] + gridGapPx));
  return Math.min(maximumColumns[mode], Math.max(1, fitting));
}

/** A small icon of the layout: two rows of cells, split into the given number of columns. */
function LayoutIcon({ columns }: { columns: number }): React.JSX.Element {
  const cellWidth = 16 / columns;
  const cells = Array.from({ length: columns * 2 }, (_, index) => ({
    x: (index % columns) * cellWidth + 1,
    y: Math.floor(index / columns) * 8 + 1,
  }));

  return (
    <svg className={styles.icon} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      {cells.map((cell) => (
        <rect key={`${cell.x}-${cell.y}`} x={cell.x} y={cell.y} width={cellWidth - 2} height={6} rx={1} />
      ))}
    </svg>
  );
}
